import * as api from "@/api";
import styles from "@/components/blocked-page.module.css";
import ButtonBlock from "@/components/button-block";
import { globals } from "@/globals";
import * as signals from "@/signals";
import { BlockedTab } from "@/types";
import { showCaptcha, showDialog, showPrompt, sleep } from "@/utils";
import clsx from "clsx";
import { t } from "i18next";
import { ChangeEvent, ClipboardEvent, JSX, useId, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface BlockedLocationState {
	autoBlock?: boolean;
	initialTab?: BlockedTab;
	reason?: string;
}

interface BlockedPageProps {
	loggedIn: () => void;
}

function BlockedPage({ loggedIn }: BlockedPageProps): JSX.Element {
	const location = useLocation();
	const locationStates = location.state as BlockedLocationState | undefined;
	const navigate = useNavigate();

	const pledgeId = useId();

	const [pledge, setPledge] = useState<string>("");
	const [pledgeInput, setPledgeInput] = useState<string>("");
	const [tab, setTab] = useState<BlockedTab>(
		locationStates?.initialTab || BlockedTab.BLOCKED,
	);

	const autoBlock = globals.params.auto === "1" || locationStates?.autoBlock;
	const reason =
		globals.params.reason ||
		locationStates?.reason ||
		t("violationOfTermsOfService");

	const applyForUnblocking = async (): Promise<void> => {
		if (!signals.login.value.username) {
			void navigate("/login", {
				replace: true,
			});
			return;
		}
		signals.isLoadingScreenShown.value = true;
		try {
			const data = await api.checkSelfUnblockEligibility();
			signals.isLoadingScreenShown.value = false;
			if (data.alert) {
				await showDialog(data.alert);
			}
			if (data.pledge) {
				await showDialog(
					t("youAreEligibleForSelfUnblock") +
						t("copyingOrPastingProhibitedOrLoseEligibility"),
					{
						countdown: 5,
						showCancel: true,
					},
				);
				setTab(BlockedTab.PLEDGE);
				setPledge(data.pledge);
			} else {
				window.open(
					"https://docs.retiehe.com/account/unblock.html#%E4%BA%BA%E5%B7%A5%E8%A7%A3%E5%B0%81",
				);
			}
		} catch (error) {
			api.handleApiError(error);
			signals.isLoadingScreenShown.value = false;
		}
	};

	const cheatingDetected = (): void => {
		void showDialog(
			t("pasteDetected") + t("youHaveLostEligibilityToSelfUnblock"),
		);
		setTab(BlockedTab.BLOCKED);
		try {
			void api.disableSelfUnblock();
		} catch (error) {
			api.handleApiError(error);
		}
	};

	const deleteBlockedAccount = async (): Promise<void> => {
		if (!signals.login.value.username) {
			void navigate("/login", {
				replace: true,
			});
			return;
		}
		await showDialog(t("confirmDeleteBlockedAccount"), {
			countdown: 30,
			showCancel: true,
		});
		await sleep(globals.ANIMATION_WAIT_TIME);
		await showDialog(t("willDeleteAllData"), {
			countdown: 5,
			showCancel: true,
		});
		await sleep(globals.ANIMATION_WAIT_TIME);
		const password = await showPrompt(t("enterThePassword"), {
			type: "password",
		});
		await sleep(globals.ANIMATION_WAIT_TIME);
		const confirmation = await showPrompt(
			t("enterToConfirmDeletion", {
				value: t("deleteAccount").toString(),
			}),
		);
		if (confirmation.toLowerCase() !== t("deleteAccount").toLowerCase()) {
			return;
		}
		const captchaResponse = await showCaptcha();
		signals.isLoadingScreenShown.value = true;
		try {
			const data = await api.deleteBlockedAccount(
				password,
				captchaResponse,
			);
			signals.isLoadingScreenShown.value = false;
			if (data.alert) {
				await showDialog(data.alert);
			}
			if (!data.success) {
				return;
			}
			signals.login.value = {};
			await showDialog(t("accountDeleted"));
			void navigate("/login", {
				replace: true,
			});
		} catch (error) {
			api.handleApiError(error);
			signals.isLoadingScreenShown.value = false;
		}
	};

	const deletePunctuation = (str?: string): string => {
		if (!str) {
			return "";
		}
		return str.replace(/[^\w\u4e00-\u9fa5]/g, "");
	};

	const handlePledgeInputChange = (
		event: ChangeEvent<HTMLTextAreaElement>,
	): void => {
		const newValue = event.target.value;
		if (pledgeInput.length === 0 && newValue === pledge) {
			cheatingDetected();
			return;
		}
		setPledgeInput(event.target.value);
	};

	const handleCopy = (event: ClipboardEvent<HTMLParagraphElement>): void => {
		event.preventDefault();
		void showDialog(t("copyingOrPastingProhibitedOrLoseEligibility"), {
			countdown: 5,
		});
	};

	const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>): void => {
		if (event.clipboardData.getData("text/plain").length < 3) {
			return;
		}
		event.preventDefault();
		cheatingDetected();
	};

	const handleSubmitClick = (): void => {
		void submitUnblock();
	};

	const submitUnblock = async (): Promise<void> => {
		const captchaResponse = await showCaptcha();
		signals.isLoadingScreenShown.value = true;
		try {
			const unblockResult = await api.selfUnblock(captchaResponse);
			signals.isLoadingScreenShown.value = false;
			if (!unblockResult.success) {
				signals.isLoadingScreenShown.value = false;
				if (unblockResult.alert) {
					void showDialog(unblockResult.alert);
				}
				return;
			}
			unblockCallback();
		} catch (error) {
			api.handleApiError(error);
			signals.isLoadingScreenShown.value = false;
		}
	};

	const unblockCallback = (): void => {
		loggedIn();
		void navigate("/", {
			replace: true,
		});
	};

	const pledgeElement = pledge.split("").map((char, index) => {
		const isCorrect =
			deletePunctuation(char) === deletePunctuation(pledgeInput[index]);
		return (
			<span
				className={clsx(
					pledgeInput.length > index &&
						isCorrect &&
						styles["correct"],
					pledgeInput.length > index &&
						!isCorrect &&
						styles["incorrect"],
				)}
				key={index}
			>
				{char}
			</span>
		);
	});

	return (
		<div className={clsx("dark", styles["blocked-page"])}>
			{tab === BlockedTab.BLOCKED && (
				<main>
					<h1 className={styles["title"]}>{t("blocked")}</h1>
					<p className={styles["description"]}>
						{t("dueToBlockReason", {
							detectedByRobot: autoBlock
								? t("detectedByRobot").toString()
								: "",
							reason: reason,
						})}
						<br />
						{t("accountBlocked")}
						<br />
						{t("ifYouHaveAnyQuestionsContactUs")}
					</p>
				</main>
			)}
			{tab === BlockedTab.PLEDGE && (
				<main>
					<label htmlFor={pledgeId}>
						{t("manuallyTypeFollowingInTextBox")}
					</label>
					<p>{t("doNotCopyOrPaste")}</p>
					<p
						className={styles["pledge"]}
						onCopy={handleCopy}
						onCut={handleCopy}
					>
						{pledgeElement}
					</p>
					<textarea
						id={pledgeId}
						placeholder={t("enterHere")}
						value={pledgeInput}
						onChange={handlePledgeInputChange}
						onPaste={handlePaste}
					></textarea>
					{deletePunctuation(pledgeInput) ===
						deletePunctuation(pledge) && (
						<ButtonBlock onClick={handleSubmitClick}>
							{t("submit")}
						</ButtonBlock>
					)}
				</main>
			)}
			<footer>
				{tab === BlockedTab.BLOCKED && (
					<button
						onClick={() => void applyForUnblocking()}
						type="button"
					>
						{t("applyForUnblocking")}
					</button>
				)}
				<button
					className={styles["danger"]}
					onClick={() => void deleteBlockedAccount()}
					type="button"
				>
					{t("deleteAccount")}
				</button>
			</footer>
		</div>
	);
}

export default BlockedPage;
